import { addDoc, collection, getDocs, writeBatch } from "firebase/firestore";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import AboutFormFields, {
  getEmptyAboutFormValues,
} from "./about_form_fields";
import { buildAboutDesignation } from "../../about/about_data";
import { toTitleCaseName } from "../../../utils/title_case";

interface UploadAboutLeaderTabProps {
  onSuccess?: () => void;
}

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

async function resequenceAboutLeaders(targetLeaderId: string) {
  const snapshot = await getDocs(collection(firestore, "about_leadership"));
  const orderedDocs = snapshot.docs
    .sort((docA, docB) => {
      const rankA =
        docA.id === targetLeaderId
          ? Number(docA.data().rank || 0)
          : Number(docA.data().rank || 0);
      const rankB =
        docB.id === targetLeaderId
          ? Number(docB.data().rank || 0)
          : Number(docB.data().rank || 0);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      if (docA.id === targetLeaderId) {
        return -1;
      }

      if (docB.id === targetLeaderId) {
        return 1;
      }

      return String(docA.data().name || "").localeCompare(String(docB.data().name || ""));
    });

  const batch = writeBatch(firestore);
  const timestamp = new Date().toISOString();

  orderedDocs.forEach((leaderDoc, index) => {
    batch.update(leaderDoc.ref, {
      rank: index + 1,
      updatedAt: timestamp,
    });
  });

  await batch.commit();
}

export default function UploadAboutLeaderTab({ onSuccess }: UploadAboutLeaderTabProps) {
  const { showSnackbar } = useFeedback();
  const [formValues, setFormValues] = useState(getEmptyAboutFormValues());
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  async function handleSubmit() {
    const rank = Number(formValues.rank);

    if (submitLockRef.current) {
      return;
    }

    if (!formValues.name.trim() || !Number.isInteger(rank) || rank <= 0) {
      showSnackbar("Please fill rank and name");
      return;
    }

    if (
      (formValues.designationType === "Youth Director" ||
        formValues.designationType === "Others") &&
      !formValues.designationDetail.trim()
    ) {
      showSnackbar("Please fill the designation detail");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSaving(true);
      const timestamp = new Date().toISOString();
      let finalImage = formValues.img.trim();

      if (selectedImage) {
        const storageRef = ref(storage, `about_leadership/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      const newLeaderRef = await addDoc(collection(firestore, "about_leadership"), {
        rank,
        name: toTitleCaseName(formValues.name),
        designationType: formValues.designationType,
        designationDetail: formValues.designationDetail.trim(),
        designation: buildAboutDesignation(
          formValues.designationType,
          formValues.designationDetail,
        ),
        place: formValues.place.trim(),
        img: normalizeUrl(finalImage),
        createdAt: timestamp,
        updatedAt: timestamp,
      });

      await resequenceAboutLeaders(newLeaderRef.id);

      setFormValues(getEmptyAboutFormValues());
      setSelectedImage(null);
      showSnackbar("Leader added successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error adding about leader", error);
      showSnackbar("Failed to add leader");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 3, boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
            <CardContent sx={{ p: 3 }}>
              <AboutFormFields formValues={formValues} setFormValues={setFormValues} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: 3, boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" flexDirection="column" gap={2.5}>
                <Box>
                  <TextField
                    fullWidth
                    label="Image URL"
                    value={formValues.img}
                    onChange={(event) => {
                      setFormValues((prev) => ({ ...prev, img: event.target.value }));
                      if (event.target.value) {
                        setSelectedImage(null);
                      }
                    }}
                    helperText="Use either an image URL or upload an image below."
                  />
                </Box>
                <FormControl fullWidth>
                  <FormLabel>Upload Leader Image</FormLabel>
                  <input
                    type="file"
                    accept="image/*"
                    style={{
                      border: "1px solid rgb(181, 176, 176)",
                      width: "96%",
                      paddingLeft: 8,
                      paddingRight: 8,
                      paddingTop: 16,
                      paddingBottom: 16,
                      borderRadius: 5,
                      marginTop: 8,
                    }}
                    onChange={(event) => {
                      if (event.target.files?.[0]) {
                        setSelectedImage(event.target.files[0]);
                        setFormValues((prev) => ({ ...prev, img: "" }));
                      }
                    }}
                  />
                  {selectedImage ? (
                    <Box mt={2} position="relative" display="inline-block">
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="Leader preview"
                        style={{ maxWidth: "220px", maxHeight: "220px", borderRadius: 8 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setSelectedImage(null)}
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          color: "#ffffff",
                          backgroundColor: "rgba(0,0,0,0.5)",
                        }}
                      >
                        <Close />
                      </IconButton>
                    </Box>
                  ) : null}
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} disabled={isSaving} sx={{ minWidth: 220 }}>
            {isSaving ? "Saving..." : "Save Leader"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
